import { isSharedStation, isSharedStationGroup } from '@app/core/stations/helpers';
import { MinimumStationsGroup, Station } from '@app/core/types/stations';

export interface SharedGroupStationSection {
  group: MinimumStationsGroup;
  stations: Station[];
}

export interface PartitionedStationSections {
  ownStations: Station[];
  individuallySharedStations: Station[];
  sharedGroupSections: SharedGroupStationSection[];
}

/**
 * Splits stations into own stations, individually shared stations, and one section
 * per shared group. A shared station that belongs to a shared group is assigned to
 * the first matching group so it is not listed twice.
 */
export function partitionStationSections(
  stations: Station[],
  stationsGroups: MinimumStationsGroup[],
): PartitionedStationSections {
  const ownStations: Station[] = [];
  const sharedStations: Station[] = [];

  stations.forEach((station) => {
    if (isSharedStation(station)) {
      sharedStations.push(station);
    } else {
      ownStations.push(station);
    }
  });

  const assignedSharedIds = new Set<string>();
  const sharedGroupSections: SharedGroupStationSection[] = [];

  stationsGroups.filter(isSharedStationGroup).forEach((group) => {
    const groupStationIds = new Set((group.stations ?? []).map((station) => station.id));
    const groupStations = sharedStations.filter(
      (station) => groupStationIds.has(station.id) && !assignedSharedIds.has(station.id),
    );

    if (groupStations.length === 0) {
      return;
    }

    groupStations.forEach((station) => assignedSharedIds.add(station.id));
    sharedGroupSections.push({ group, stations: groupStations });
  });

  const individuallySharedStations = sharedStations.filter(
    (station) => !assignedSharedIds.has(station.id),
  );

  return {
    ownStations,
    individuallySharedStations,
    sharedGroupSections,
  };
}
